import { CoreSwitch } from '@components'
import InfoIcon from '@images/icons/info-circle-black.svg?react'
import { showToast } from '@utils'
import cn from 'classnames'
import { useMerchantSettingsQry, useUpdateHideSmallBalancesMutation } from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import { useEffect, useState } from 'react'
import ReactTooltip from 'react-tooltip'
import styles from './HideSmallBalancesToggle.module.scss'

export const HideSmallBalancesToggle = ({ className }: { className?: string }) => {
  const { settings } = useMerchantSettingsQry()
  const [updateHideSmallBalances] = useUpdateHideSmallBalancesMutation()

  // Optimistic value, held until the settings refetch confirms it (or the request fails).
  // Releasing it when the request resolves would flash the old value until the refetch lands.
  const [pending, setPending] = useState<boolean | null>(null)
  const savedValue = Boolean(settings?.hideSmallBalances)
  const isActive = pending ?? savedValue

  useEffect(() => {
    if (pending !== null && pending === savedValue) setPending(null)
  }, [pending, savedValue])

  const handleSwitch = () => {
    const next = !isActive
    setPending(next)
    updateHideSmallBalances(next)
      .unwrap()
      .catch(() => {
        setPending(null)
        showToast(
          'error',
          tr('FRONTEND.ACCOUNTS.HIDE_SMALL_BALANCES.ERROR', 'Could not update the setting. Please try again.')
        )
      })
  }

  const label = tr('FRONTEND.ACCOUNTS.HIDE_SMALL_BALANCES.LABEL', 'Hide small balances')

  const tooltip = tr('FRONTEND.ACCOUNTS.HIDE_SMALL_BALANCES.TOOLTIP', 'Hide accounts < 1 USD')

  return (
    <div className={cn(styles['hide-small-balances-wrapper'], 'hide-small-balances-wrapper', className)}>
      <div
        className={cn(styles['hide-small-balances'], 'hide-small-balances')}
        role='switch'
        aria-checked={isActive}
        aria-label={label}
        data-test='hide-small-balances-toggle'
        tabIndex={0}
        onClick={handleSwitch}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleSwitch()
          }
        }}
      >
        {/* the whole element is the click target; clicks on the switch bubble up to it */}
        <CoreSwitch isActive={isActive} handleSwitch={() => undefined} />
        <span className={cn(styles['hide-small-balances-title'], 'hide-small-balances-title')}>{label}</span>
      </div>
      {/* sibling of the switch (not a child) so hovering/clicking the icon never toggles the setting */}
      <InfoIcon
        className={cn(styles['hide-small-balances-info'], 'hide-small-balances-info')}
        data-for='hide-small-balances-tooltip'
        data-tip={tooltip}
        data-test='hide-small-balances-info'
        role='img'
        aria-label={tooltip}
      />
      <ReactTooltip
        id='hide-small-balances-tooltip'
        place='top'
        effect='solid'
        className='mm-tooltip-primary mm-tooltip-small'
      />
    </div>
  )
}
